import { ChevronDown, ChevronRight, RefreshCw } from 'lucide-react';
import { type ReactNode, useState } from 'react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

export interface ExpandSection {
  key: string;
  label: string;
  count?: number;
  load: () => Promise<void>;
  loaded: boolean;
  loading: boolean;
  render: () => ReactNode;
  refresh?: () => Promise<void>;
}

/**
 * Generic collapsible list of related-data sections.
 *
 * Each section knows how to lazy-load itself on first open. Re-opens reuse the
 * cached data; the optional `refresh` button forces a re-fetch. The component
 * keeps its own open/closed state — callers only need to pass the section
 * spec list.
 */
export function ExpandPanel({ sections }: { sections: ExpandSection[] }) {
  const [open, setOpen] = useState<Record<string, boolean>>({});

  if (sections.length === 0) {
    return <div className="text-xs text-muted-foreground italic px-2 py-1">no relations</div>;
  }

  return (
    <div className="space-y-1 border-l-2 border-border ml-2 pl-2">
      {sections.map((s) => {
        const isOpen = open[s.key] ?? false;
        return (
          <div key={s.key}>
            <div
              className={cn(
                'flex items-center gap-1 text-sm rounded hover:bg-muted',
                s.loading && 'opacity-50'
              )}
            >
              <button
                type="button"
                onClick={async () => {
                  if (!s.loaded && !s.loading) await s.load();
                  setOpen((o) => ({ ...o, [s.key]: !o[s.key] }));
                }}
                className="flex flex-1 items-center gap-1 px-2 py-1 text-left"
              >
                {isOpen ? (
                  <ChevronDown className="h-3 w-3" />
                ) : (
                  <ChevronRight className="h-3 w-3" />
                )}
                <span>{s.label}</span>
                {s.count !== undefined && (
                  <span className="text-muted-foreground text-xs">({s.count})</span>
                )}
                {s.loading && <RefreshCw className="h-3 w-3 animate-spin ml-auto" />}
              </button>
              {s.loaded && s.refresh && (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon-xs"
                  className="mr-1 h-5 w-5"
                  onClick={() => {
                    void s.refresh?.();
                  }}
                  aria-label={`Refresh ${s.label}`}
                >
                  <RefreshCw className="h-3 w-3" />
                </Button>
              )}
            </div>
            {isOpen && s.loaded && <div className="ml-4 mt-1">{s.render()}</div>}
          </div>
        );
      })}
    </div>
  );
}
